import { sendMessage, authState } from '@composables/useWindowEvents.js'
import { REPLICATION_DELAY_MS, AUTH_BASE_URL } from '@config'
import { RESOURCE_COLLECTIONS } from '@config/options.js'
import * as sdkInterface from '@plugins/sdkInterface.js'
import { addToast } from '@composables/useToast.js'
import useRoute from '@composables/useRoute.js'
import { LOGIN_METHODS } from '@config/auth.js'
import { asyncSleep } from '@helpers/utils.js'
import i18n from '@plugins/localisation.js'
import qs from 'qs'
import {
  getMyFollowedCollectionsRequest,
  getMyUserFollowingsRequest,
  getMySubscriptionsRequest,
  getMyBlockedUsersRequest,
  getMyMutedUsersRequest,
  getMyPurchasesRequest,
  getCogsTeamsRequest,
  getAllUserRatings,
  getUserRequest,
  createClient,
} from '@services'
import {
  collectionFollowingStore,
  userFollowingStore,
  subscriptionStore,
  blockedUsersStore,
  mutedUsersStore,
  purchasesStore,
  myTeamsStore,
  ratingStore,
  authStore,
} from '@stores'
import {
  PUBLIC_BASE_URL,
  LOGOUT_ENDPOINT,
  PUBLIC_UDT_URL,
  Settings,
  GAME_ID,
} from '@config/game.js'

const { clearUser, setToken, setUser, isLoggedIn, user: myUser } = authStore()
const { setMyFollowedCollections } = collectionFollowingStore()
const { setMyFollowedUsers } = userFollowingStore()
const { setMySubscriptions } = subscriptionStore()
const { setBlockedUsers } = blockedUsersStore()
const { setMyPurchases } = purchasesStore()
const { setMutedUsers } = mutedUsersStore()
const { setUserRatings } = ratingStore()
const { setMyTeams } = myTeamsStore()
const { get, post } = createClient()

/* API request to update local storage data */
export async function updateLocalStorage(user, noCogs = false) {
  const promises = [
    {
      req: user ? Promise.resolve(user) : getUserRequest(),
      callback: (user) => setUser({ ...user, no_cogs: noCogs }),
    },
    {
      req: getMySubscriptionsRequest(),
      callback: (res) => setMySubscriptions(res.data),
    },
    {
      req: getAllUserRatings(),
      callback: (res) => setUserRatings(res.data),
    },
    {
      req: getAllUserRatings({
        resource_type: RESOURCE_COLLECTIONS,
      }),
      callback: (res) => setUserRatings(res.data, true),
    },
    {
      req: getMyUserFollowingsRequest(),
      callback: (res) => setMyFollowedUsers(res.data),
    },
    {
      req: getMyMutedUsersRequest(),
      callback: (res) => setMutedUsers(res.data),
    },
  ]

  if (!noCogs) {
    promises.push({
      req: getMyBlockedUsersRequest(user?.info?.id || myUser.value?.info?.id),
      callback: (res) => setBlockedUsers(res.data),
    })

    promises.push({
      req: getCogsTeamsRequest(),
      callback: (data) =>
        setMyTeams(data.filter((team) => team.game_id === GAME_ID)),
    })
  }

  if (Settings.value.showMarketplace)
    promises.push({
      req: getMyPurchasesRequest(),
      callback: (res) => setMyPurchases(res.data),
    })

  if (Settings.value.showCollections)
    promises.push({
      req: getMyFollowedCollectionsRequest(),
      callback: (res) => setMyFollowedCollections(res.data),
    })

  const results = await Promise.allSettled(promises.map((p) => p.req))

  if (
    results.some((res) => res.status === 'rejected' && res.reason?.code === 401)
  ) {
    // unauthorised
    clearUser()
    const t = i18n.global.t
    addToast({
      title: t('login_fail'),
      isError: true,
      text: t('login_fail_message'),
    })
    throw new Error()
  }

  promises.forEach((p, i) => {
    if (results[i]?.status === 'fulfilled') {
      p.callback(results[i].value)
    }
  })
}

/* API request to request code */
export async function requestCodeRequest({ email } = {}) {
  const { data } = await post(
    `${AUTH_BASE_URL}/emailrequest`,
    qs.stringify({ email, game_id: GAME_ID }),
    { withCredentials: true }
  )
  return data
}

/* API request to login using code */
export async function loginRequest({ code } = {}) {
  const {
    data: { access_token, user },
  } = await post(
    `${AUTH_BASE_URL}/emailexchange`,
    qs.stringify({ code, game_id: GAME_ID }),
    {
      withCredentials: true,
    }
  )

  await boot({ access_token, user })
}

/* API request to retrieve oauth login redirect url */
export async function oAuthUrlRequest(service, gameId = null) {
  let url = _getUrl(service)
  if (!url) return

  if (gameId) {
    url += `?game_id=${gameId}`
  }

  const { data } = await get(`${AUTH_BASE_URL}${url}`, {
    withCredentials: true,
  })

  if (data.url) {
    return data.url
  }
}

/* API request code login user with oauth */
export async function oAuthLoginRequest(service, path) {
  try {
    const url = _getUrl(service)
    if (!url || !service || !path) {
      throw new Error()
    }

    const query = path.replace(/[^?]*/, '')

    const {
      data: { access_token, user },
    } = await get(`${AUTH_BASE_URL}${url}${query}&game_id=${GAME_ID}`, {
      withCredentials: true,
    })

    if (access_token) {
      return await boot({ access_token, user })
    } else {
      throw new Error()
    }
  } catch (error) {
    clearUser()
    throw error
  }
}

/* API request login with User Delegated Token */
export async function udtLoginRequest(udt, skipSdk = false) {
  try {
    const {
      data: { access_token },
    } = await post(`${PUBLIC_UDT_URL}/external/udtauth`, null, {
      headers: {
        'X-Modio-Delegation-Token': udt,
      },
    })

    if (access_token) {
      return await boot({ access_token, noCogs: true, skipSdk })
    } else {
      throw new Error()
    }
  } catch (error) {
    clearUser()
    throw error
  }
}

/* Generate a User Delegation Token for the current user */
export async function generateUDTRequest() {
  const { data } = await post(
    `${PUBLIC_BASE_URL}/me/s2s/oauth/token`,
    qs.stringify({ entity: 'auth_server' })
  )
  return data
}

/* API request to get SSO Info (name/icon) */
export async function getSSOInfoRequest() {
  const { data } = await get(
    `${AUTH_BASE_URL}/studio/info?game_id=${GAME_ID}`,
    {
      withCredentials: true,
    }
  )

  return data
}

/* API request to logout */
export async function logoutRequest() {
  if (!isLoggedIn.value) return

  try {
    await post(LOGOUT_ENDPOINT)
  } catch (err) {
    console.error(err)
  } finally {
    clearUser()

    const { routeMeta, push } = useRoute()
    if (routeMeta.value?.requiresAuth) {
      push({ path: '/' })
    }
  }
}

async function boot({ access_token, user, noCogs, skipSdk }) {
  // handle replication delay
  await asyncSleep(REPLICATION_DELAY_MS)

  try {
    clearUser()
    setToken(access_token)

    if (skipSdk !== true) sdkInterface.setAuthToken()

    await updateLocalStorage(user, noCogs)
  } catch (error) {
    console.error(error)
  }
}

export async function exchangeIDtokenForAccessToken(id_token, email) {
  const update = { id_token }
  if (email) {
    update.email = email
  }

  try {
    const {
      data: { access_token },
    } = await post(
      `${PUBLIC_BASE_URL}/external/openidauth`,
      qs.stringify(update)
    )

    await boot({ access_token })
  } catch (error) {
    console.error(error)
    authState.value.error = error
    sendMessage({ modioAuth: 'refresh' })
    addToast({
      title: i18n.global.t('login_fail'),
      isError: true,
      text: error,
    })
  }
}

function _getUrl(type) {
  const loginMethod = LOGIN_METHODS.find((method) => method.type === type)

  if (loginMethod?.url) {
    return loginMethod.url
  } else {
    throw new Error('Invalid login service')
  }
}
